// Groups nearby detections into a single timeline marker, mirroring holodeck's clusterDetections
// (src/components/Timeline/utils.ts). Detections are sorted by time, then each one is chained into the
// current cluster when it falls within thresholdSeconds of the PREVIOUS detection - so a run of nearby
// detections collapses into one marker. A cluster sits at the average of its detections' timestamps and
// takes the colour of its most common detection type. Holodeck's live threshold (TimelineWrapper) is 1s.

export interface TimelineDetection {
  id: string
  type: string
  timestampMs: number
}

export interface DetectionCluster {
  id: string
  timestampMs: number // average of the clustered detections' timestamps
  detections: TimelineDetection[]
  primaryType: string // most common type in the cluster
  count: number
}

export function clusterDetections(detections: TimelineDetection[], thresholdSeconds = 1): DetectionCluster[] {
  if (detections.length === 0) return []

  // Shallow copy so we don't mutate the caller's array.
  const sorted = [...detections].sort((a, b) => a.timestampMs - b.timestampMs)
  const clusters: DetectionCluster[] = []

  let current: TimelineDetection[] = [sorted[0]]
  let typeCounts = new Map<string, number>([[sorted[0].type, 1]])
  let timestampSum = sorted[0].timestampMs

  const finalize = () => {
    let primaryType = current[0].type
    let maxCount = 0
    for (const [type, count] of typeCounts) {
      if (count > maxCount) {
        maxCount = count
        primaryType = type
      }
    }
    clusters.push({
      id: `cluster_${clusters.length}`,
      timestampMs: Math.round(timestampSum / current.length),
      detections: current,
      primaryType,
      count: current.length
    })
  }

  for (let i = 1; i < sorted.length; i++) {
    const detection = sorted[i]
    const last = current[current.length - 1]
    const gapSeconds = (detection.timestampMs - last.timestampMs) / 1000
    if (gapSeconds <= thresholdSeconds) {
      current.push(detection)
      typeCounts.set(detection.type, (typeCounts.get(detection.type) ?? 0) + 1)
      timestampSum += detection.timestampMs
    } else {
      finalize()
      current = [detection]
      typeCounts = new Map([[detection.type, 1]])
      timestampSum = detection.timestampMs
    }
  }
  finalize()

  return clusters
}
